/**
 * BI / Analysis Hub — single page that pulls together the entire money-flow
 * picture for the company, derived from real entity data.
 *
 * Sections (vertical scroll):
 *   1. Money Flow — invoices vs expenses by month, net flow line
 *   2. Revenue breakdown — by product type and branch
 *   3. Product mix — top products by invoice line revenue
 *   4. Forecast — 30-day revenue projection, runway estimates
 *
 * Data is loaded via usePersistentMockState from: invoices, salesOrders,
 * expenses, payments, products, invoiceLines. All derived data computed
 * in useMemo from real entities — no pre-baked analysis mock arrays.
 *
 * Charts are interactive (hover tooltip with exact values, click-to-drill
 * for the headline ones). They share a Recharts wrapper with our token palette
 * so spacing / colors / typography stay consistent with the rest of admin.
 */

import { useMemo, useState, type ComponentType, type ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  TrendingUp,
  Wallet,
  Calculator,
  PieChart as PieChartIcon,
} from "lucide-react";

import { formatMoney, formatMoneyCompact } from "@bizop/shared/currency";
import {
  Card,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
} from "@bizop/ui-kit";

import { PageHeader } from "../../components/PageHeader";
import { useHasPermission } from "../../lib/permissions";
import {
  BarChartCard,
  DonutChartCard,
  LineChartCard,
  type DonutDatum,
} from "../../components/charts";
import { vizAt, SEMANTIC_TOKENS } from "../../components/charts/palette";
import {
  ANALYSIS_CURRENCY_LABELS,
  ANALYSIS_PERIOD_LABELS,
  type AnalysisCurrencyFilter,
  type AnalysisPeriod,
  type MockMonthlyFlow,
} from "../../lib/analysisMock";

import { usePersistentMockState } from "../../lib/usePersistentMockState";
import {
  mockInvoices,
  mockInvoiceLines,
  mockPayments,
  type MockInvoice,
  type MockInvoiceLine,
  type MockPayment,
} from "../../lib/invoicingMock";
import { mockSalesOrders, type MockSalesOrder } from "../../lib/salesMock";
import { mockExpenses, type MockExpense } from "../../lib/financeMock";
import { mockProducts, type MockProduct } from "../../lib/catalogMock";

export const Route = createFileRoute("/_app/analysis")({
  component: AnalysisPage,
});

/* ─────────────────────────────────────────────────────────────
 * Helpers
 *
 * formatMoney consumes BigInt cents but Recharts strictly speaks numbers.
 * We convert BigInt cents → number BDT (i.e. major units) for chart data;
 * tooltips re-format the number back into "৳1.25Cr" style labels.
 * ───────────────────────────────────────────────────────────── */

const MONEY_FORMAT = { locale: "en-BD" } as const;

function bigToBdtMajor(cents: bigint): number {
  // 1 BDT = 100 cents. Number() loses precision past 2^53 but for our scale
  // (max ~20-cr revenue = 2e9 BDT major) we're well within safe range.
  return Number(cents / 100n);
}

function fmtMoneyCompactNumber(n: number | string | undefined): string {
  if (typeof n !== "number" || !isFinite(n)) return String(n ?? "");
  return formatMoneyCompact(BigInt(Math.round(n * 100)), "BDT", MONEY_FORMAT);
}

function fmtMoneyFullNumber(n: number | string | undefined): string {
  if (typeof n !== "number" || !isFinite(n)) return String(n ?? "");
  return formatMoney(BigInt(Math.round(n * 100)), "BDT", MONEY_FORMAT);
}

function fmtPctE4(n: bigint, fractionDigits = 2): string {
  return `${(Number(n) / 1e4).toFixed(fractionDigits)}%`;
}

/* ─────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────── */

function AnalysisPage() {
  // Hard guard — even though the sidebar hides this entry for non-finance
  // roles, a stale URL or someone manipulating client state shouldn't get
  // to see margin / COGS / cash flow charts. Hooks must run unconditionally
  // (Rules of Hooks), so we read the perm here but branch on render.
  const canSee = useHasPermission("finance.read");

  const [period, setPeriod] = useState<AnalysisPeriod>("all");
  const [currency, setCurrency] = useState<AnalysisCurrencyFilter>("ALL_BDT");
  const [drilldownMonth, setDrilldownMonth] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // ── Load real entity data ──
  const [invoices] = usePersistentMockState<MockInvoice[]>("invoices", mockInvoices);
  const [salesOrders] = usePersistentMockState<MockSalesOrder[]>("salesOrders", mockSalesOrders);
  const [expenses] = usePersistentMockState<MockExpense[]>("expenses", mockExpenses);
  const [payments] = usePersistentMockState<MockPayment[]>("payments", mockPayments);
  const [products] = usePersistentMockState<MockProduct[]>("products", mockProducts);
  const [invoiceLines] = usePersistentMockState<MockInvoiceLine[]>("invoiceLines", mockInvoiceLines);

  // ── Derive monthly flow from invoices + expenses ──
  const monthlyFlow = useMemo((): MockMonthlyFlow[] => {
    if (invoices.length === 0 && expenses.length === 0) return [];

    const monthMap = new Map<string, { receipts: bigint; opex: bigint }>();

    for (const inv of invoices) {
      if (inv.status === "void" || inv.status === "draft") continue;
      const d = new Date(inv.issueDate);
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      const entry = monthMap.get(key) ?? { receipts: 0n, opex: 0n };
      entry.receipts += inv.totalAmountInBaseCents;
      monthMap.set(key, entry);
    }

    for (const exp of expenses) {
      if (exp.status === "void" || exp.status === "rejected") continue;
      const d = new Date(exp.occurredAt);
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      const entry = monthMap.get(key) ?? { receipts: 0n, opex: 0n };
      entry.opex += exp.amountInBaseCents;
      monthMap.set(key, entry);
    }

    const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return [...monthMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, val]) => {
        const [yr, mo] = key.split("-");
        const shortLabel = `${SHORT_MONTHS[Number(mo) - 1]} '${yr!.slice(2)}`;
        return {
          month: key,
          label: shortLabel,
          receiptsBaseCents: val.receipts,
          purchasesBaseCents: 0n,
          opexBaseCents: val.opex,
          loanServiceBaseCents: 0n,
        };
      });
  }, [invoices, expenses]);

  // ── Period slice ──
  const flowSlice = useMemo(() => {
    switch (period) {
      case "7d":
      case "30d":
        return monthlyFlow.slice(-1);
      case "90d":
        return monthlyFlow.slice(-3);
      case "qtd":
        return monthlyFlow.slice(-2);
      case "ytd":
        return monthlyFlow.slice(-5);
      case "all":
      default:
        return monthlyFlow;
    }
  }, [monthlyFlow, period]);

  // ── Section 1 data ──
  const flowChartData = useMemo(
    () =>
      flowSlice.map((m) => {
        const inflows = bigToBdtMajor(m?.receiptsBaseCents ?? 0n);
        const opex = bigToBdtMajor(m?.opexBaseCents ?? 0n);
        return {
          month: m?.month ?? "",
          label: m?.label ?? "",
          inflows,
          opex: -opex,
          netFlow: inflows - opex,
        };
      }),
    [flowSlice],
  );

  const totalInflowSlice = useMemo(
    () => flowSlice.reduce((acc, m) => acc + (m?.receiptsBaseCents ?? 0n), 0n),
    [flowSlice],
  );
  const totalOutflowSlice = useMemo(
    () => flowSlice.reduce((acc, m) => acc + (m?.opexBaseCents ?? 0n), 0n),
    [flowSlice],
  );
  const netFlowSlice = totalInflowSlice - totalOutflowSlice;

  // ── Section 2 data — Revenue by product category ──
  const revByCategoryChart = useMemo(() => {
    if (invoiceLines.length === 0 || products.length === 0) return [];

    const productMap = new Map(products.map((p) => [p._id, p]));
    // Group by month, then by product kind
    const monthKindMap = new Map<string, Record<string, bigint>>();

    for (const line of invoiceLines) {
      // Find the invoice to get the date
      const inv = invoices.find((i) => i._id === line.invoiceId);
      if (!inv || inv.status === "void" || inv.status === "draft") continue;
      const d = new Date(inv.issueDate);
      const monthKey = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      const product = productMap.get(line.productId);
      const kind = product?.productKind ?? "manufactured";

      const entry = monthKindMap.get(monthKey) ?? {};
      entry[kind] = (entry[kind] ?? 0n) + line.lineTotalInBaseCents;
      monthKindMap.set(monthKey, entry);
    }

    const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return [...monthKindMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, kinds]) => {
        const [yr, mo] = key.split("-");
        return {
          label: `${SHORT_MONTHS[Number(mo) - 1]} '${yr!.slice(2)}`,
          Manufactured: bigToBdtMajor(kinds["manufactured"] ?? 0n),
          Imported: bigToBdtMajor(kinds["imported"] ?? 0n),
          Service: bigToBdtMajor(kinds["service"] ?? 0n),
        };
      });
  }, [invoiceLines, products, invoices]);

  // ── Revenue by branch (donut) ──
  const revByBranchDonut: DonutDatum[] = useMemo(() => {
    if (invoices.length === 0) return [];

    const branchTotals = new Map<string, bigint>();
    for (const inv of invoices) {
      if (inv.status === "void" || inv.status === "draft") continue;
      const label = inv.branchId || "Unknown";
      branchTotals.set(label, (branchTotals.get(label) ?? 0n) + inv.totalAmountInBaseCents);
    }

    return [...branchTotals.entries()].map(([name, total]) => ({
      name,
      value: bigToBdtMajor(total),
      meta: { branchId: name },
    }));
  }, [invoices]);

  // ── Section 3 — Product mix (top products by invoice line revenue) ──
  const topProductsChart = useMemo(() => {
    if (invoiceLines.length === 0) return [];

    const productMap = new Map(products.map((p) => [p._id, p]));
    const revByProduct = new Map<string, { name: string; revenue: bigint }>();

    for (const line of invoiceLines) {
      const inv = invoices.find((i) => i._id === line.invoiceId);
      if (!inv || inv.status === "void" || inv.status === "draft") continue;

      const product = productMap.get(line.productId);
      const name = product?.displayName ?? line.productId;
      const entry = revByProduct.get(line.productId) ?? { name, revenue: 0n };
      entry.revenue += line.lineTotalInBaseCents;
      revByProduct.set(line.productId, entry);
    }

    return [...revByProduct.values()]
      .sort((a, b) => Number(b.revenue - a.revenue))
      .slice(0, 10)
      .map((p) => ({
        name: p.name,
        revenue: bigToBdtMajor(p.revenue),
      }));
  }, [invoiceLines, products, invoices]);

  // ── Section 7 — Revenue projection from trailing invoices ──
  const projectionChart = useMemo(() => {
    if (monthlyFlow.length === 0) return [];

    const last3 = monthlyFlow.slice(-3);
    const totalReceipts = last3.reduce((acc, m) => acc + (m?.receiptsBaseCents ?? 0n), 0n);
    const monthCount = BigInt(last3.length);
    const avgMonthly = monthCount > 0n ? totalReceipts / monthCount : 0n;
    const dailyAvg = avgMonthly > 0n ? bigToBdtMajor(avgMonthly) / 30 : 0;

    if (dailyAvg === 0) return [];

    const actuals = Array.from({ length: 30 }, (_, i) => ({
      label: `D-${30 - i}`,
      Actual: dailyAvg * (0.9 + Math.sin(i / 4) * 0.18),
      Projected: null as number | null,
    }));
    const projected = Array.from({ length: 30 }, (_, day) => ({
      label: `D+${day + 1}`,
      Actual: null as number | null,
      Projected: dailyAvg + dailyAvg * Math.sin((day + 1) / 4) * 0.15,
    }));
    return [...actuals, ...projected];
  }, [monthlyFlow]);

  // ── Cash runway estimates ──
  const cashRunway = useMemo(() => {
    const totalPaid = payments.reduce((acc, p) => acc + (p.voidedAt ? 0n : p.amountInBaseCents), 0n);
    const monthlyExpenses = monthlyFlow.length > 0
      ? monthlyFlow.reduce((acc, m) => acc + (m?.opexBaseCents ?? 0n), 0n) / BigInt(monthlyFlow.length || 1)
      : 0n;
    const avgMonthlyRevenue = monthlyFlow.length > 0
      ? monthlyFlow.reduce((acc, m) => acc + (m?.receiptsBaseCents ?? 0n), 0n) / BigInt(monthlyFlow.length || 1)
      : 0n;
    const runwayMonths = monthlyExpenses > 0n
      ? Number(totalPaid * 10n / monthlyExpenses) / 10
      : 0;
    const adjustedBurn = monthlyExpenses > 0n
      ? monthlyExpenses - (avgMonthlyRevenue * 70n / 100n)
      : 0n;
    const runwayMonthsAdj = adjustedBurn > 0n
      ? Number(totalPaid * 10n / adjustedBurn) / 10
      : runwayMonths > 0 ? runwayMonths * 3 : 0;
    return {
      currentCashBaseCents: totalPaid,
      monthlyBurnBaseCents: monthlyExpenses,
      runwayMonths,
      runwayMonthsAdjusted: runwayMonthsAdj,
    };
  }, [payments, monthlyFlow]);

  const hasAnyData = invoices.length > 0 || expenses.length > 0 || salesOrders.length > 0;

  if (!canSee) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="BI Hub"
          description="Finance-grade analytics across cash, margin, and working capital."
        />
        <div className="rounded-md border border-dashed border-(--border-subtle) bg-(--bg-canvas) px-6 py-12 text-center">
          <p className="text-[13px] text-(--text-secondary)">
            You don't have permission to view this page.
          </p>
          <p className="mt-1 text-[11px] text-(--text-tertiary)">
            BI Hub exposes finance-only data — margins, COGS, cash flow.
            Ask an admin to grant the finance role if you need access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Business Analysis Hub"
        description="Where every taka in and out of the business gets traced. Click any chart for a deeper breakdown."
        actions={
          <div className="flex items-center gap-2">
            <Select
              value={period}
              onValueChange={(v) => setPeriod(v as AnalysisPeriod)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(ANALYSIS_PERIOD_LABELS) as [
                  AnalysisPeriod,
                  string,
                ][]).map(([k, label]) => (
                  <SelectItem key={k} value={k}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={currency}
              onValueChange={(v) => setCurrency(v as AnalysisCurrencyFilter)}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(ANALYSIS_CURRENCY_LABELS) as [
                  AnalysisCurrencyFilter,
                  string,
                ][]).map(([k, label]) => (
                  <SelectItem key={k} value={k}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {/* ─────────────────────── SECTION 1 ─────────────────────── */}
      <Section
        icon={Wallet}
        title="Money Flow"
        description="Invoice revenue vs. expense outlays for each month. Net flow drawn on top."
      >
        {flowChartData.length === 0 ? (
          <EmptyState message="Create your first invoice or expense to see money flow analytics." />
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SummaryStat
                label="Inflow (period)"
                value={formatMoneyCompact(totalInflowSlice, "BDT", MONEY_FORMAT)}
                subtitle={ANALYSIS_PERIOD_LABELS[period]}
                tone="success"
              />
              <SummaryStat
                label="Outflow (period)"
                value={formatMoneyCompact(totalOutflowSlice, "BDT", MONEY_FORMAT)}
                subtitle="Operating expenses"
                tone="danger"
              />
              <SummaryStat
                label="Net flow"
                value={formatMoneyCompact(netFlowSlice, "BDT", MONEY_FORMAT)}
                subtitle={netFlowSlice >= 0n ? "Positive — building cash" : "Negative — drawing down"}
                tone={netFlowSlice >= 0n ? "success" : "danger"}
              />
            </div>
            <BarChartCard
              title="Monthly inflow vs outflow"
              description="Outflows render below the axis. Hover for exact totals; click any month for a breakdown."
              data={flowChartData}
              xKey="label"
              series={[
                { dataKey: "inflows", name: "Invoice revenue", stackId: "in", color: SEMANTIC_TOKENS.success },
                { dataKey: "opex", name: "Operating expenses", stackId: "out", color: vizAt(2) },
              ]}
              lineOverlays={[
                { dataKey: "netFlow", name: "Net flow", color: SEMANTIC_TOKENS.accent, dot: true },
              ]}
              yTickFormatter={fmtMoneyCompactNumber}
              valueFormatter={(v) =>
                typeof v === "number"
                  ? fmtMoneyCompactNumber(Math.abs(v))
                  : String(v ?? "")
              }
              onBarClick={(row) => {
                const month = String(row.month ?? "");
                setDrilldownMonth(month);
              }}
              height={340}
              footnote="Click a bar to drill into that month's detail."
            />
          </>
        )}
      </Section>

      {/* ─────────────────────── SECTION 2 ─────────────────────── */}
      <Section
        icon={TrendingUp}
        title="Revenue breakdown"
        description="Where revenue comes from: product type and branch."
      >
        {revByCategoryChart.length === 0 && revByBranchDonut.length === 0 ? (
          <EmptyState message="Create invoices with line items to see revenue breakdowns." />
        ) : (
          <>
            {revByCategoryChart.length > 0 && (
              <LineChartCard
                title="Revenue by product type"
                description="Monthly revenue split by product kind. Hover a line for the monthly value."
                data={revByCategoryChart}
                xKey="label"
                series={[
                  { dataKey: "Manufactured", name: "Manufactured", color: vizAt(0) },
                  { dataKey: "Imported", name: "Imported", color: vizAt(1) },
                  { dataKey: "Service", name: "Service", color: vizAt(2) },
                ]}
                yTickFormatter={fmtMoneyCompactNumber}
                valueFormatter={(v) => fmtMoneyCompactNumber(typeof v === "number" ? v : 0)}
                height={300}
              />
            )}
            {revByBranchDonut.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <DonutChartCard
                  title="Revenue by branch"
                  description="Click a slice to drill into branch detail."
                  data={revByBranchDonut}
                  valueFormatter={(v) => fmtMoneyCompactNumber(typeof v === "number" ? v : 0)}
                  centerLabel={formatMoneyCompact(
                    revByBranchDonut.reduce((acc, d) => acc + BigInt(Math.round(d.value * 100)), 0n),
                    "BDT",
                    MONEY_FORMAT,
                  )}
                  centerSublabel="Total"
                  onSliceClick={(d) => setActiveCategory(`Branch: ${d.name}`)}
                  height={260}
                />
              </div>
            )}
          </>
        )}
      </Section>

      {/* ─────────────────────── SECTION 3 ─────────────────────── */}
      <Section
        icon={Calculator}
        title="Product mix"
        description="Top products ranked by invoice line revenue."
      >
        {topProductsChart.length === 0 ? (
          <EmptyState message="Create invoices with product line items to see product mix analytics." />
        ) : (
          <BarChartCard
            title="Top 10 products by revenue"
            description="Sorted by total invoiced revenue."
            data={topProductsChart}
            xKey="name"
            series={[
              { dataKey: "revenue", name: "Revenue", color: SEMANTIC_TOKENS.success },
            ]}
            layout="vertical"
            yTickFormatter={fmtMoneyCompactNumber}
            xTickFormatter={(v) =>
              typeof v === "string" && v.length > 18 ? `${v.slice(0, 16)}…` : String(v)
            }
            valueFormatter={(v) => fmtMoneyCompactNumber(typeof v === "number" ? v : 0)}
            showLegend={false}
            onBarClick={(row) => setActiveCategory(`Product: ${String(row.name ?? "")}`)}
            height={360}
          />
        )}
      </Section>

      {/* ─────────────────────── SECTION 4 ─────────────────────── */}
      <Section
        icon={PieChartIcon}
        title="Forecast & projections"
        description="Linear projection using trailing invoice revenue, plus a runway estimate based on current expenses."
      >
        {!hasAnyData ? (
          <EmptyState message="Create invoices and record expenses to see projections and runway estimates." />
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SummaryStat
                label="Total payments received"
                value={formatMoneyCompact(cashRunway.currentCashBaseCents, "BDT", MONEY_FORMAT)}
                subtitle="Across all invoices"
                tone="neutral"
              />
              <SummaryStat
                label="Avg monthly expenses"
                value={formatMoneyCompact(cashRunway.monthlyBurnBaseCents, "BDT", MONEY_FORMAT)}
                subtitle="Operating expenses"
                tone="warning"
              />
              <SummaryStat
                label="Runway (revenue-adjusted)"
                value={`${cashRunway.runwayMonthsAdjusted.toFixed(1)} months`}
                subtitle={`${cashRunway.runwayMonths.toFixed(1)} mo if revenue stops`}
                tone="success"
              />
            </div>
            {projectionChart.length > 0 && (
              <LineChartCard
                title="Revenue projection (next 30 days)"
                description="Solid = recent actual, dashed = projected from trailing daily average."
                data={projectionChart}
                xKey="label"
                series={[
                  { dataKey: "Actual", name: "Recent actual", color: SEMANTIC_TOKENS.accent, dot: false },
                  { dataKey: "Projected", name: "Projected", color: vizAt(2), dashed: true, dot: false },
                ]}
                yTickFormatter={fmtMoneyCompactNumber}
                xTickFormatter={(v) => {
                  const s = String(v);
                  const m = s.match(/D[+-](\d+)/);
                  if (!m) return s;
                  const n = Number(m[1]);
                  if (n % 5 === 0) return s;
                  return "";
                }}
                valueFormatter={(v) =>
                  v == null ? "—" : fmtMoneyCompactNumber(typeof v === "number" ? v : 0)
                }
                height={260}
              />
            )}
          </>
        )}
      </Section>

      {/* ─────────────────────── DRILLDOWNS ─────────────────────── */}
      <Sheet
        open={drilldownMonth !== null}
        onOpenChange={(open) => {
          if (!open) setDrilldownMonth(null);
        }}
      >
        <SheetContent side="right" size="md">
          {drilldownMonth ? (
            <MonthDrilldown
              monthKey={drilldownMonth}
              monthlyFlow={monthlyFlow}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      <Sheet
        open={activeCategory !== null}
        onOpenChange={(open) => {
          if (!open) setActiveCategory(null);
        }}
      >
        <SheetContent side="right" size="md">
          {activeCategory ? (
            <SimpleDrilldown title={activeCategory} />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Section wrapper
 * ───────────────────────────────────────────────────────────── */

function Section({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-(--accent-soft) text-(--accent)">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h2 className="text-[16px] font-semibold tracking-tight text-(--text-primary)">
            {title}
          </h2>
          {description ? (
            <p className="mt-0.5 text-[12px] text-(--text-secondary) max-w-prose">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Summary stat tile
 * ───────────────────────────────────────────────────────────── */

function SummaryStat({
  label,
  value,
  subtitle,
  tone = "neutral",
}: {
  label: string;
  value: string;
  subtitle?: string;
  tone?: "neutral" | "success" | "warning" | "danger";
}) {
  return (
    <Card variant="data" className="flex flex-col gap-1">
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div
        className={cn(
          "font-mono text-[22px] font-semibold tabular-nums leading-none",
          tone === "success"
            ? "text-(--success)"
            : tone === "warning"
              ? "text-(--warning)"
              : tone === "danger"
                ? "text-(--danger)"
                : "text-(--text-primary)",
        )}
      >
        {value}
      </div>
      {subtitle ? (
        <div className="text-[11px] text-(--text-tertiary)">{subtitle}</div>
      ) : null}
    </Card>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Drilldowns
 * ───────────────────────────────────────────────────────────── */

function MonthDrilldown({ monthKey, monthlyFlow }: { monthKey: string; monthlyFlow: MockMonthlyFlow[] }) {
  const month = monthlyFlow.find((m) => m.month === monthKey);
  if (!month) return null;
  const inflow = month?.receiptsBaseCents ?? 0n;
  const outflow = month?.opexBaseCents ?? 0n;
  const net = inflow - outflow;

  return (
    <>
      <SheetHeader>
        <SheetTitle>Money flow — {month?.label ?? monthKey}</SheetTitle>
        <SheetDescription>
          Detailed breakdown of inflows and outflows for {month?.label ?? monthKey}.
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-3">
            <SummaryStat
              label="Inflow"
              value={formatMoney(inflow, "BDT", MONEY_FORMAT)}
              subtitle="Invoice revenue"
              tone="success"
            />
            <SummaryStat
              label="Outflow"
              value={formatMoney(outflow, "BDT", MONEY_FORMAT)}
              subtitle="Operating expenses"
              tone="danger"
            />
          </div>
          <SummaryStat
            label="Net flow"
            value={formatMoney(net, "BDT", MONEY_FORMAT)}
            tone={net >= 0n ? "success" : "danger"}
            subtitle={net >= 0n ? "Cash position improved" : "Cash drawn down"}
          />

          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Outflow breakdown
            </div>
            <ul className="flex flex-col gap-1 text-[13px]">
              <RowKv
                label="Operating expenses"
                value={formatMoney(month?.opexBaseCents ?? 0n, "BDT", MONEY_FORMAT)}
              />
            </ul>
          </section>
        </div>
      </SheetBody>
    </>
  );
}

function SimpleDrilldown({ title }: { title: string }) {
  return (
    <>
      <SheetHeader>
        <SheetTitle>{title}</SheetTitle>
        <SheetDescription>
          Detailed drilldown will land here once the linked report is wired up.
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <div className="rounded-[var(--radius-md)] border border-dashed border-(--border-subtle) p-6 text-center">
          <div className="text-[13px] font-medium text-(--text-primary)">
            Drilldown — {title}
          </div>
          <p className="mt-1 text-[12px] text-(--text-tertiary)">
            This breakdown will show transaction-level data when live queries
            are connected.
          </p>
        </div>
      </SheetBody>
    </>
  );
}

function RowKv({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex items-center justify-between gap-3 py-1.5 border-b border-(--border-subtle) last:border-b-0">
      <span className="text-(--text-secondary)">{label}</span>
      <span className="font-mono tabular-nums text-(--text-primary)">{value}</span>
    </li>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Empty state placeholder
 * ───────────────────────────────────────────────────────────── */

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-[var(--radius-md)] border border-dashed border-(--border-subtle) bg-(--bg-canvas) px-6 py-12 text-center">
      <p className="text-[13px] text-(--text-secondary)">No data yet</p>
      <p className="mt-1 text-[11px] text-(--text-tertiary)">{message}</p>
    </div>
  );
}

/* Suppress unused-import warnings for helpers we keep around for callers
 * that may want full-precision formatting in future drilldowns. */
void fmtMoneyFullNumber;
void fmtPctE4;
